import { trapFocus } from './utils';

/*
Fixed Header on Scroll
*/
const siteHeader = document.querySelector('.site-header');
const scrollThreshold = 200; // Pixels to scroll before fixing header

if (siteHeader) {
    let ticking = false;
    const isOverlay = siteHeader.classList.contains('header-overlay');
    let headerHeight = siteHeader.offsetHeight;

    const updateHeader = () => {
        const currentScrollY = window.scrollY;

        if (currentScrollY > scrollThreshold) {
            if (!siteHeader.classList.contains('is-fixed')) {
                siteHeader.classList.add('is-fixed');
                // Add body padding to prevent content jump (only for non-overlay headers)
                if (!isOverlay) {
                    document.body.style.paddingTop = `${headerHeight}px`;
                }
            }
        } else {
            if (siteHeader.classList.contains('is-fixed')) {
                siteHeader.classList.remove('is-fixed');
                // Remove body padding
                if (!isOverlay) {
                    document.body.style.paddingTop = '';
                }
            }
        }

        ticking = false;
    };

    // Update header height on resize
    window.addEventListener('resize', () => {
        headerHeight = siteHeader.offsetHeight;
    }, { passive: true });

    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(updateHeader);
            ticking = true;
        }
    }, { passive: true });

    // Check initial scroll position on load
    updateHeader();
}

/*
Mobile UI
*/
const menuToggle = document.getElementById('menu-toggle');
const primaryNav = document.getElementById('primary-navigation');

if (menuToggle && primaryNav) {
    menuToggle.addEventListener('click', e => {
        e.preventDefault();
        const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true';
        
        menuToggle.setAttribute('aria-expanded', !isExpanded);
        primaryNav.classList.toggle('is-open');
        document.body.classList.toggle('menu-open');
    });

    // Close menu when clicking outside
    document.addEventListener('click', e => {
        if (!menuToggle.contains(e.target) && !primaryNav.contains(e.target)) {
            if (primaryNav.classList.contains('is-open')) {
                menuToggle.setAttribute('aria-expanded', 'false');
                primaryNav.classList.remove('is-open');
                document.body.classList.remove('menu-open');
            }
        }
    });

    // Close menu on escape key
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && primaryNav.classList.contains('is-open')) {
            menuToggle.setAttribute('aria-expanded', 'false');
            primaryNav.classList.remove('is-open');
            document.body.classList.remove('menu-open');
            menuToggle.focus();
        }
    });
}

/*
Submenu Toggle (Desktop) - Click to open, focus trap for accessibility
*/
class SubmenuController {
    constructor() {
        this.activeSubmenu = null;
        this.activeToggle = null;
        this.releaseFocusTrap = null;
        
        this.init();
    }

    init() {
        const submenuToggles = document.querySelectorAll('.submenu-toggle');
        
        submenuToggles.forEach(toggle => {
            toggle.addEventListener('click', e => this.handleToggleClick(e, toggle));
            toggle.addEventListener('keydown', e => this.handleToggleKeydown(e, toggle));
        });

        // Close submenu when clicking outside
        document.addEventListener('click', e => this.handleOutsideClick(e));

        // Global keydown for escape
        document.addEventListener('keydown', e => this.handleGlobalKeydown(e));
    }

    handleToggleClick(e, toggle) {
        e.preventDefault();
        e.stopPropagation();
        
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const parentItem = toggle.closest('.menu-item-has-children');
        const submenu = parentItem?.querySelector('.sub-menu');

        if (!submenu) return;

        // If clicking a different toggle, close the current one first
        if (this.activeToggle && this.activeToggle !== toggle) {
            this.closeSubmenu();
        }

        if (isExpanded) {
            this.closeSubmenu();
        } else {
            this.openSubmenu(toggle, submenu);
        }
    }

    handleToggleKeydown(e, toggle) {
        const parentItem = toggle.closest('.menu-item-has-children');
        const submenu = parentItem?.querySelector('.sub-menu');

        if (!submenu) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                this.handleToggleClick(e, toggle);
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (toggle.getAttribute('aria-expanded') !== 'true') {
                    this.openSubmenu(toggle, submenu);
                }
                break;
            case 'Escape':
                if (toggle.getAttribute('aria-expanded') === 'true') {
                    e.preventDefault();
                    this.closeSubmenu();
                    toggle.focus();
                }
                break;
        }
    }

    openSubmenu(toggle, submenu) {
        toggle.setAttribute('aria-expanded', 'true');
        submenu.classList.add('is-open');
        
        this.activeToggle = toggle;
        this.activeSubmenu = submenu;

        // Set up focus trap using the utility function
        // Focus is locked within submenu - user must press Escape or click outside to exit
        this.releaseFocusTrap = trapFocus(submenu, {
            focusFirst: true,
            focusDelay: 50,
            onEscape: () => {
                const toggleToFocus = this.activeToggle;
                this.closeSubmenu();
                toggleToFocus?.focus();
            }
            // No onExitTop/onExitBottom - Tab cycles within the submenu
        });
    }

    closeSubmenu() {
        if (this.activeToggle) {
            this.activeToggle.setAttribute('aria-expanded', 'false');
        }
        
        if (this.activeSubmenu) {
            this.activeSubmenu.classList.remove('is-open');
        }

        // Release focus trap
        if (this.releaseFocusTrap) {
            this.releaseFocusTrap();
            this.releaseFocusTrap = null;
        }

        this.activeSubmenu = null;
        this.activeToggle = null;
    }

    handleOutsideClick(e) {
        if (!this.activeSubmenu || !this.activeToggle) return;

        const parentItem = this.activeToggle.closest('.menu-item-has-children');
        
        if (parentItem && !parentItem.contains(e.target)) {
            this.closeSubmenu();
        }
    }

    handleGlobalKeydown(e) {
        if (e.key === 'Escape' && this.activeSubmenu) {
            const toggleToFocus = this.activeToggle;
            this.closeSubmenu();
            toggleToFocus?.focus();
        }
    }
}

// Initialize submenu controller
document.addEventListener('DOMContentLoaded', () => {
    new SubmenuController();
});
